import React from 'react';
import { useApp } from '../context/AppContext';
import type { Character, Spell } from '../types';
import { BackButton } from '../components/BackButton';
import { paths } from '../navigation/paths';
import { ClassMark } from '../components/Pictogram';

function levelOneValue(
  formula: string | undefined,
  character: Character,
  fallbackFormula: string,
  calculateDamage: (formula: string | undefined, character: Character, fallback: string) => number,
) {
  return Math.round(calculateDamage(formula, { ...character, level: 1 }, fallbackFormula));
}

export function CharacterSelectionPage() {
  const app = useApp();
  const availableCharacters = (app.availableCharacters as Character[]) || [];
  const handleSelectCharacter = app.handleSelectCharacter as (id: number) => void;
  const calculateDamage = app.calculateDamage as (formula: string | undefined, character: Character, fallback: string) => number;
  const fallback = paths.play;

  return (
    <div className="character-selection medieval">
      <header className="character-header">
        <h1>⚔️ Wähle deinen Helden</h1>
        <p className="subtitle">
          {app.playerName ? `${app.playerName} wählt einen Charakter` : 'Wähle einen Charakter für dein Abenteuer'}
        </p>
        <p className="info-text">💡 Tipp: Mehrere Spieler können denselben Charakter wählen</p>
        <BackButton fallback={fallback} className="medieval-button">
          ← Zurück<span className="btn-extra"> zu den Spielen</span>
        </BackButton>
      </header>

      <div className="characters-grid">
        {availableCharacters.length === 0 ? (
          <div className="no-characters">
            <p>Keine Charaktere verfügbar.</p>
            <p>Der Spielleiter muss zuerst Charaktere erstellen.</p>
          </div>
        ) : (
          availableCharacters.map(char => {
            const damage = levelOneValue(char.damage_formula, char, '5+strength*0.5', calculateDamage);
            const movement = levelOneValue(char.movement_formula, char, '3+dexterity*0.1', calculateDamage);
            return (
            <div key={char.id} className={`character-card combat-card character ${char.is_taken ? 'taken' : ''}`}>
              <div className="character-portrait combat-card-avatar">
                <ClassMark
                  klass={char.class}
                  icon={app.classes?.find(c => c.name === char.class)?.icon}
                  className="character-icon"
                />
              </div>
              <h3>{char.name}</h3>
              <p className="character-class">{char.class}</p>

              <div className="character-stats">
                <div className="stat-row">
                  <span>💪 Staerke:</span> <strong>{char.strength || 10}</strong>
                </div>
                <div className="stat-row">
                  <span>🎯 Geschick:</span> <strong>{char.dexterity || 10}</strong>
                </div>
                <div className="stat-row">
                  <span>🧠 Intelligenz:</span> <strong>{char.intelligence || 10}</strong>
                </div>
                <div className="stat-row">
                  <span>❤️ Konstitution:</span> <strong>{char.constitution || 10}</strong>
                </div>
                <div className="stat-row derived">
                  <span>⚔ Schaden:</span> <strong>{damage}</strong>
                </div>
                <div className="stat-row derived">
                  <span>➤ Bewegung:</span> <strong>{movement}</strong>
                </div>
                <p className="level-one-note">Formel auf Stufe 1</p>
              </div>

              {char.special_ability && (
                <div className="special-ability">
                  <strong>Spezialfaehigkeit:</strong>
                  <p>{char.special_ability}</p>
                </div>
              )}

              {char.description && (
                <div className="character-description">
                  <p>{char.description}</p>
                </div>
              )}

              {char.spells && char.spells.length > 0 && (
                <div className="character-abilities-preview">
                  <strong>✨ Faehigkeiten:</strong>
                  <ul>
                    {(char.spells as Spell[]).map(spell => (
                      <li key={spell.id}>
                        <span className="ability-name">{spell.name}</span>
                        <span className="ability-cost">({spell.mana_cost} Mana)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                className="select-button"
                onClick={() => handleSelectCharacter(char.id)}
              >
                ⚔️ Auswaehlen
              </button>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}
